import type {
  AssistantCard,
  BlockedReason,
  FeedAttachment,
  PartnerAssistantEvent,
  PlanDto,
} from "./types";

export type FeedItem =
  | { kind: "user"; id: string; text: string; attachments: FeedAttachment[] }
  | {
      kind: "assistant";
      id: string;
      text: string;
      cards: AssistantCard[];
      ask: { question: string; options: string[] } | null;
      plan: PlanDto | null;
      offTopic: boolean;
      error: boolean;
      pending: boolean;
    };

export interface FeedState {
  conversationId: string | null;
  items: FeedItem[];
  busy: boolean;
  blocked: BlockedReason | null;
  /** Сервер отклонил «Сделать» (план уже выполнен, отменён, заменён или не подтверждён). */
  notice: "planAction" | null;
}

export type FeedAction =
  /**
   * initial: восстановление диалога при открытии; не применяется, если
   * сотрудник уже начал ход (лента не пуста или идёт поток).
   */
  | {
      type: "hydrate";
      conversationId: string;
      items: FeedItem[];
      initial?: boolean;
    }
  | { type: "sent"; id: string; text: string; attachments: FeedAttachment[] }
  | { type: "planStarted"; planId: string }
  | { type: "planRejected"; planId: string }
  /**
   * «Сделать» не дошло до конца: 401/403/5xx до потока или поток оборван
   * без plan_status. Карточка возвращается в черновик (настоящий статус
   * подтягивает перезапрос диалога), blocked ставит причину блокировки.
   */
  | { type: "planFailed"; planId: string; blocked: BlockedReason | null }
  | { type: "event"; event: PartnerAssistantEvent }
  | { type: "failed" }
  | { type: "reset" };

export const initialFeedState: FeedState = {
  conversationId: null,
  items: [],
  busy: false,
  blocked: null,
  notice: null,
};

type AssistantItem = Extract<FeedItem, { kind: "assistant" }>;

let pendingSeq = 0;

const newAssistant = (): AssistantItem => ({
  kind: "assistant",
  id: `pending-${Date.now()}-${++pendingSeq}`,
  text: "",
  cards: [],
  ask: null,
  plan: null,
  offTopic: false,
  error: false,
  pending: true,
});

function updateLast(
  items: FeedItem[],
  fn: (item: AssistantItem) => AssistantItem
): FeedItem[] {
  const index = items.map((i) => i.kind).lastIndexOf("assistant");
  if (index < 0) return items;
  return items.map((item, i) =>
    i === index ? fn(item as AssistantItem) : item
  );
}

function updatePlan(
  items: FeedItem[],
  planId: string,
  fn: (plan: PlanDto) => PlanDto
): FeedItem[] {
  return items.map((item) =>
    item.kind === "assistant" && item.plan?.id === planId
      ? { ...item, plan: fn(item.plan) }
      : item
  );
}

const isEmptyAnswer = (it: AssistantItem) =>
  !it.text && !it.cards.length && !it.plan && !it.ask && !it.error;

/**
 * Ответ-заглушка больше не ждёт событий: пустой убирается, с содержимым
 * (текст, карточки, план, вопрос, ошибка) остаётся и перестаёт ждать.
 */
function settlePending(items: FeedItem[]): FeedItem[] {
  return items.flatMap((it) => {
    if (it.kind !== "assistant" || !it.pending) return [it];
    return isEmptyAnswer(it) ? [] : [{ ...it, pending: false }];
  });
}

/** Отметить ошибкой ответ, который ещё ждёт событий (последний pending). */
function failPending(items: FeedItem[]): FeedItem[] {
  return updateLast(items, (it) =>
    it.pending ? { ...it, pending: false, error: true } : it
  );
}

function onEvent(state: FeedState, e: PartnerAssistantEvent): FeedState {
  switch (e.type) {
    case "conversation":
      return { ...state, conversationId: e.conversationId };
    case "delta":
      return {
        ...state,
        items: updateLast(state.items, (it) => ({
          ...it,
          text: it.text + e.text,
        })),
      };
    case "ask":
      return {
        ...state,
        items: updateLast(state.items, (it) => ({
          ...it,
          ask: { question: e.question, options: e.options },
        })),
      };
    case "plan": {
      // Новый план заменяет прежний черновик (бэкенд отменяет его сам).
      const items = state.items.map((it) =>
        it.kind === "assistant" && it.plan?.status === "DRAFT"
          ? { ...it, plan: { ...it.plan, status: "CANCELLED" as const } }
          : it
      );
      return {
        ...state,
        items: updateLast(items, (it) => ({ ...it, plan: e.plan })),
      };
    }
    case "plan_progress":
      return {
        ...state,
        items: updatePlan(state.items, e.planId, (p) => ({
          ...p,
          steps: p.steps.map((s) =>
            s.index === e.index ? { ...s, status: e.status, error: e.error } : s
          ),
        })),
      };
    case "plan_status":
      return {
        ...state,
        items: updatePlan(state.items, e.planId, (p) => ({
          ...p,
          status: e.status,
        })),
      };
    case "entity_cards":
      return {
        ...state,
        items: updateLast(state.items, (it) => ({
          ...it,
          cards: [...it.cards, ...e.cards],
        })),
      };
    case "off_topic":
      return {
        ...state,
        items: updateLast(state.items, (it) => ({ ...it, offTopic: true })),
      };
    case "blocked":
      return {
        ...state,
        items: settlePending(state.items),
        busy: false,
        blocked: e.reason,
      };
    case "error":
      return {
        ...state,
        items: updateLast(state.items, (it) => ({ ...it, error: true })),
      };
    case "done":
      return {
        ...state,
        busy: false,
        items: updateLast(state.items, (it) => ({
          ...it,
          // Сбой до записи ответа приходит с пустым messageId.
          id: e.messageId || it.id,
          pending: false,
        })),
      };
  }
}

export function feedReducer(state: FeedState, action: FeedAction): FeedState {
  switch (action.type) {
    case "reset":
      return initialFeedState;
    case "hydrate":
      if (action.initial && (state.items.length > 0 || state.busy)) {
        return state;
      }
      return {
        ...initialFeedState,
        notice: state.notice,
        conversationId: action.conversationId,
        items: action.items,
      };
    case "sent":
      return {
        ...state,
        busy: true,
        blocked: null,
        notice: null,
        items: [
          ...settlePending(state.items),
          {
            kind: "user",
            id: action.id,
            text: action.text,
            attachments: action.attachments,
          },
          newAssistant(),
        ],
      };
    case "planStarted":
      return {
        ...state,
        busy: true,
        notice: null,
        items: [
          ...updatePlan(settlePending(state.items), action.planId, (p) => ({
            ...p,
            status: "RUNNING",
          })),
          newAssistant(),
        ],
      };
    case "planRejected":
      // Ответ 400/409 пришёл до потока: карточка не должна остаться «Выполняется».
      return {
        ...state,
        busy: false,
        notice: "planAction",
        items: updatePlan(settlePending(state.items), action.planId, (p) => ({
          ...p,
          status: "DRAFT",
        })),
      };
    case "planFailed":
      return {
        ...state,
        busy: false,
        blocked: action.blocked ?? state.blocked,
        items: updatePlan(
          action.blocked
            ? settlePending(state.items)
            : failPending(state.items),
          action.planId,
          (p) => ({ ...p, status: "DRAFT" })
        ),
      };
    case "failed":
      return {
        ...state,
        busy: false,
        items: failPending(state.items),
      };
    case "event":
      return onEvent(state, action.event);
  }
}
